// PIN API for the idle-lock overlay. The PIN itself is hashed server-side (see
// internal/auth.UserStore.SetPIN); the client only learns "is a PIN set" and
// gets a yes/no on verify.
//
// pinEnabled is a reactive signal mirroring whether the current user has a PIN
// set. lock.js reads it to decide whether to arm the lock overlay timer (no
// PIN → no overlay; the overlay can't be dismissed without one). The signal
// updates whenever someone calls refreshPinStatus(), setPin(), or clearPin(),
// so toggling PIN from Settings re-arms / disables the lock without a reload.
import { createSignal } from 'solid-js';
import { api } from './api.js';

const [pinEnabled, setPinEnabled] = createSignal(false);
export { pinEnabled };

// Fetch /api/me/pin and sync the signal. Returns the raw status object so
// existing callers (Settings's createResource) keep working unchanged.
export async function refreshPinStatus() {
  try {
    const r = await api.get('/api/me/pin');
    setPinEnabled(!!r?.enabled);
    return r;
  } catch (_) {
    // On failure (e.g. logged out, network), treat as "no PIN" so the lock
    // doesn't engage based on stale state — safer to under-lock than to trap
    // the user behind an unverifiable overlay.
    setPinEnabled(false);
    return { enabled: false };
  }
}

// Backwards-compatible alias — Settings.jsx passes this to createResource.
export const getPinStatus = refreshPinStatus;

export async function setPin(pin) {
  const r = await api.put('/api/me/pin', { pin });
  setPinEnabled(true);
  return r;
}

export async function clearPin() {
  const r = await api.delete('/api/me/pin');
  setPinEnabled(false);
  return r;
}

export const verifyPin = (pin) => api.post('/api/me/pin/verify', { pin });
